import {
    IconArrowNarrowLeftDashed,
    IconCircleDotted,
    IconArrowRight,
    IconChartBar,
    IconTrendingDown,
    IconTrendingUp,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { Button, Card, Text } from '@fragments';
import Parser from '@helpers/parser';

import { CardSkeleton } from '../Skeletons';

import './styles.scss';

// Compact major-unit label for the y-axis (R$ 1,2k · R$ 30k · R$ 1,5M).
function compact(cents, currency) {
    const value = (Number(cents) || 0) / 100;
    const symbol = String(currency).toUpperCase() === 'USD' ? 'US$' : 'R$';
    if (value >= 1_000_000) return `${symbol}${(value / 1_000_000).toFixed(1).replace('.0', '').replace('.', ',')}M`;
    if (value >= 1_000) return `${symbol}${(value / 1_000).toFixed(1).replace('.0', '').replace('.', ',')}k`;
    return `${symbol}${Math.round(value)}`;
}

/**
 * GrowthCard — month-over-month revenue growth for the active owner community,
 * in the "Budget Overview" grammar: a KPI row (this month + delta pill · last
 * month) above a pure-CSS monthly bar chart with a muted axis. The current
 * month is the single accent bar; history is grayscale.
 *
 * Data is the sales report's revenue series bucketed by month (last 6 months).
 * `split` hands the header / body / footer to the owner bento as separate
 * blocks (see Card).
 */
export default function GrowthCard({ growth, loading, communityId, split = false }) {
    const { t } = useTranslation('dashboard');

    const currency = growth?.currency || 'BRL';
    const months = growth?.months || [];
    const max = Math.max(1, ...months.map(m => m.value));
    const hasData = months.some(m => m.value > 0);

    const delta = growth?.growth;
    const trend = delta == null ? null : delta >= 0 ? 'up' : 'down';

    // Four evenly spaced axis ticks from max down to 0.
    const ticks = [1, 0.66, 0.33, 0].map(f => compact(max * f, currency));

    return (
        <Card
            flat={!split}
            split={split}
            headerDivider
            className="growth-card"
            title={t('home.growth.title')}
            subtitle={t('home.growth.subtitle')}
            icon={<IconChartBar />}
            footer={
                <Button
                    color="inverse"
                    variant="text"
                    size="small"
                    fullWidth
                    endIcon={<IconArrowRight />}
                    onClick={() => window.navigate(communityId ? `/insights?community=${communityId}` : '/insights')}
                >
                    {t('home.growth.fullReport')}
                </Button>
            }
        >
            {loading ? (
                <div className="growth-card__skeleton">
                    <CardSkeleton height={64} />
                    <CardSkeleton height={170} />
                </div>
            ) : (
                <div className="growth-card__body">
                    <div className="growth-card__kpis">
                        <div className="growth-card__kpi">
                            <Text.subheading contrast={6} left={<IconCircleDotted />}>{t('home.growth.thisMonth')}</Text.subheading>
                            <div className="growth-card__value-row">
                                <Text.h5 weight={500} contrast={10}>
                                    {Parser.price.format(growth?.current ?? 0, currency)}
                                </Text.h5>
                                {trend && (
                                    <span className={`growth-card__delta growth-card__delta--${trend}`}>
                                        {trend === 'up' ? <IconTrendingUp /> : <IconTrendingDown />}
                                        <Text.caption>{`${delta > 0 ? '+' : ''}${delta}%`}</Text.caption>
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="growth-card__kpi">
                            <Text.subheading contrast={6} left={<IconArrowNarrowLeftDashed />}>{t('home.growth.previousMonth')}</Text.subheading>
                            <div className="growth-card__value-row">
                                <Text.h5 weight={500} contrast={9}>
                                    {Parser.price.format(growth?.previous ?? 0, currency)}
                                </Text.h5>
                            </div>
                        </div>
                    </div>

                    <div className="growth-card__chart" role="img" aria-label={t('home.growth.chartAria')}>
                        <div className="growth-card__axis" aria-hidden="true">
                            {ticks.map((t, i) => <Text.caption key={i} contrast={6}>{t}</Text.caption>)}
                        </div>

                        <div className="growth-card__bars">
                            {months.map((m, i) => {
                                const isCurrent = i === months.length - 1;
                                const height = hasData ? Math.max(2, Math.round((m.value / max) * 100)) : 2;
                                return (
                                    <div className="growth-card__bar-wrap" key={`${m.label}-${i}`} title={Parser.price.format(m.value, currency)}>
                                        <div className="growth-card__bar">
                                            <span
                                                className={`growth-card__bar-fill${isCurrent ? ' growth-card__bar-fill--current' : ''}`}
                                                style={{ height: `${height}%`, animationDelay: `${i * 40}ms` }}
                                            />
                                        </div>
                                        <Text.caption contrast={isCurrent ? 9 : 6}>{m.label}</Text.caption>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {!hasData && (
                        <Text.caption contrast={5} className="growth-card__empty">
                            {t('home.growth.empty')}
                        </Text.caption>
                    )}
                </div>
            )}
        </Card>
    );
}
